import React from 'react';

import classNames from 'classnames';

import {
    GROUP_START_CELL,
    isDarkReportRow,
    reportNegativeColourClass,
    reportRowFillClass,
    reportRowIndentClass,
    reportRowWeightClass,
} from './helpers/reportRowStyles';
import { varianceColourClass } from './helpers/varianceColour';
import { COL_COMMITTED, COL_FORECAST, COL_OPPORTUNITY, COL_PERCENT, COL_RISK, COL_UNCOMMITTED } from './types/ReportColumns';
import { ROW_COST_CENTRE, ROW_DEPARTMENT, ROW_TOTAL } from './types/ReportRowType';
import { NOT_IN_CURRENT_STRUCTURE_ID } from './types/ReportType';

import { formatFigure } from 'Helpers/formatService';

const DASH = '—';

const SUPPLEMENTARY_ORDER = [COL_COMMITTED, COL_UNCOMMITTED, COL_PERCENT, COL_RISK, COL_OPPORTUNITY, COL_FORECAST];

// Comparison cells carry a pale tint so they read as a block apart from the
// report version's own figures; total rows take the lighter blue so the tint
// still shows against their fill.
const AB_TINT = {
    [ROW_TOTAL]: '!tw-bg-blue-75',
};

const AB_TINT_DEFAULT = '!tw-bg-blue-10';

// A comparison figure is "no equivalent line" when null/undefined — rendered as
// a dash, distinct from a budgeted nil (the zero dash).
const formatComparison = (value) => (value === null || value === undefined ? DASH : formatFigure(value));

const ActualVsBudgetReportSummaryTableRow = (props) => {
    const columns = props.visibleColumns || {};
    const isUnmatched = props.data.department.id === NOT_IN_CURRENT_STRUCTURE_ID;
    // Per-section count carried on the sentinel row itself — the same field the
    // Full-details sentinel reads. Not the report-wide unmatchedComparisonCount,
    // which covers revenue and expenditure together.
    const unmatchedLines = props.data.data.total?.unmatched_comparison_count ?? 0;
    const rowType = props.isTotalRow ? ROW_TOTAL : props.isSubRow ? ROW_COST_CENTRE : ROW_DEPARTMENT;
    const firstVisibleSupplementary = SUPPLEMENTARY_ORDER.find((key) => columns[key]);

    // The "Not in current structure" row exists only in the comparison version,
    // so every current-structure figure is a dash and only AB Budget carries a
    // value. Every other row formats its figures normally.
    const money = (value) => (isUnmatched ? DASH : formatFigure(value));

    // Variance cells are coloured favourable (green) / adverse (red); the sense
    // inverts for revenue and net rows. Total/net rows have dark fills → lighter
    // tints. The unmatched row shows dashes, so it gets no colour.
    const varClass = (value) => (isUnmatched ? '' : varianceColourClass(value, props.favourableWhenNegative, isDarkReportRow(rowType)));

    // A plain figure cell. Negatives are already parenthesised by the formatter;
    // the colour reinforces that they are unfavourable.
    const figure = (value, { groupStart = false } = {}) => (
        <td
            className={classNames('inbud-numeric-cell', isUnmatched ? '' : reportNegativeColourClass(value, rowType), {
                [GROUP_START_CELL]: groupStart,
            })}
        >
            {money(value)}
        </td>
    );

    // A variance cell: coloured by whether the variance is favourable, not by sign.
    const variance = (value) => <td className={classNames('inbud-numeric-cell', varClass(value))}>{money(value)}</td>;

    // AB (comparison) lifetime columns — tinted to stand apart from the report
    // version's own columns, with a per-tier tint so the row banding still reads.
    const comparison = (props.data.data.total && props.data.data.total.comparison) || null;
    const abCellClass = classNames('inbud-numeric-cell', AB_TINT[rowType] || AB_TINT_DEFAULT);
    const abBudget = comparison ? formatComparison(comparison.total) : DASH;
    // The unmatched row shows only its AB budget; its variance stays a dash.
    const abVariance = !isUnmatched && comparison ? formatComparison(comparison.variance) : DASH;

    return (
        <tr className={classNames('tw-text-[12.5px]', props.className || reportRowFillClass(rowType), reportRowWeightClass(rowType))}>
            <td
                className={classNames(reportRowIndentClass(rowType), {
                    'tw-italic !tw-font-normal !tw-text-charcoal-50': isUnmatched,
                })}
            >
                {props.data.department.name}
                {isUnmatched && (
                    <>
                        {/* How many comparison lines this one row stands in for.
                            Full details has always shown this; the summary tiers
                            were the odd one out. Same field, same wording. */}
                        {` (${unmatchedLines === 1 ? $t('1 line') : $t('{{count}} lines', { count: unmatchedLines })})`}
                        {/* The unmatched row overrides its fill to a light background,
                            so it takes the light-row sub-label tint regardless of tier. */}
                        <span className="tw-block tw-text-3xs tw-not-italic tw-text-charcoal-50">
                            {$t('Present in {{version}} only', { version: props.comparisonName })}
                        </span>
                    </>
                )}
            </td>
            {figure(props.data.data.month.total, { groupStart: true })}
            {figure(props.data.data.month.total_actual)}
            {variance(props.data.data.month.variance)}
            {figure(props.data.data.period.total, { groupStart: true })}
            {figure(props.data.data.period.total_actual)}
            {variance(props.data.data.period.variance)}
            {figure(props.data.data.total.total, { groupStart: true })}
            {figure(props.data.data.total.total_actual)}
            {variance(props.data.data.total.variance)}
            {props.comparisonActive && (
                <>
                    <td className={abCellClass}>{abBudget}</td>
                    <td className={classNames(abCellClass, varClass(comparison ? comparison.variance : null))}>{abVariance}</td>
                </>
            )}
            {columns[COL_COMMITTED] && figure(props.data.data.committed.total, { groupStart: firstVisibleSupplementary === COL_COMMITTED })}
            {columns[COL_UNCOMMITTED] && figure(props.data.data.uncommitted.total, { groupStart: firstVisibleSupplementary === COL_UNCOMMITTED })}
            {columns[COL_PERCENT] && figure(props.data.data.uncommitted.percent_variance, { groupStart: firstVisibleSupplementary === COL_PERCENT })}
            {columns[COL_RISK] && figure(props.data.data.risk.total ?? 0, { groupStart: firstVisibleSupplementary === COL_RISK })}
            {columns[COL_OPPORTUNITY] && figure(props.data.data.opp.total ?? 0, { groupStart: firstVisibleSupplementary === COL_OPPORTUNITY })}
            {columns[COL_FORECAST] && figure(props.data.data.afc.total ?? 0, { groupStart: firstVisibleSupplementary === COL_FORECAST })}
        </tr>
    );
};

export default ActualVsBudgetReportSummaryTableRow;
